import { Controller } from "@hotwired/stimulus"
import { Transformer } from "markmap-lib"
import { Markmap } from "markmap-view"

export default class extends Controller {
  static targets = ["svg", "content"]

  static values = {
    markdown: { type: String, default: "" }
  }

  connect() {
    this.transformer = new Transformer()
    this.markmap = null

    // Get markdown content from the hidden content element or from value
    const markdown = this.getMarkdownContent()
    if (markdown) {
      this.renderMarkmap(markdown)
    }

    // Handle window resize
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener("resize", this.resizeHandler)
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    if (this.markmap) {
      this.markmap.destroy()
    }
  }

  getMarkdownContent() {
    // First try to get from the content target
    if (this.hasContentTarget) {
      return this.contentTarget.textContent.trim()
    }
    // Otherwise use the value
    return this.markdownValue
  }

  renderMarkmap(markdown) {
    if (!this.hasSvgTarget) return

    // Clear any existing content
    this.svgTarget.innerHTML = ""

    // Transform markdown to markmap data
    const { root } = this.transformer.transform(markdown)

    // Create the markmap
    this.markmap = Markmap.create(this.svgTarget, {
      autoFit: true,
      duration: 500,
      maxWidth: 300,
      paddingX: 20,
      spacingVertical: 10,
      spacingHorizontal: 120,
      color: (node) => {
        // Use brand colors for different depth levels
        const colors = [
          "#7147FF", // Root - brand purple
          "#5A36E6", // Level 1 - darker purple
          "#4b5563", // Level 2 - gray
          "#6b7280", // Level 3 - lighter gray
        ]
        return colors[Math.min(node.state.depth, colors.length - 1)]
      }
    }, root)

    // Fit the view after a short delay to ensure proper sizing
    setTimeout(() => {
      if (this.markmap) {
        this.markmap.fit()
      }
    }, 100)
  }

  handleResize() {
    if (this.markmap) {
      this.markmap.fit()
    }
  }

  // Method to update markdown content dynamically
  updateMarkdown(markdown) {
    this.markdownValue = markdown
    this.renderMarkmap(markdown)
  }

  // Zoom controls
  zoomIn() {
    if (this.markmap) {
      this.markmap.rescale(1.25)
    }
  }

  zoomOut() {
    if (this.markmap) {
      this.markmap.rescale(0.8)
    }
  }

  fit() {
    if (this.markmap) {
      this.markmap.fit()
    }
  }
}
